import type { RefObject } from 'react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { MultiValue, SingleValue, GroupBase, SelectInstance, StylesConfig, CSSObjectWithLabel } from 'react-select'
import { components } from 'react-select'
import type { AsyncProps } from 'react-select/async'
import cx from 'clsx'

import { IconCirclePlus, IconUser, IconX } from '@tabler/icons-react'
import Select, { useSelectHelpers } from '@components/Form/ReactAsyncSelect'
import Button from '@components/Button'
import { usePopupHandlers } from '@components/Popup'
import QuickUserAddPopup from '@pages/Team/Members/components/QuickUserAddPopup'

import { useQueryClient } from '@tanstack/react-query'

import debounce from '@helpers/debounce'
import { fetchTeamDropdownList, teamDropdownQueryKey, useGetUsersByIds } from '@queries/OrgStructure'
import { OrgStructureKeys } from '@queries/Keys'
import { useGetConnectedChannel } from '@helpers/hooks/useGetConnectedChannel'
import useImportUsersFromChannel from '@pages/Team/Members/hooks/useImportUsersFromChannel'
import type { CommunicationType } from '@src/constants'
import { communicationTypes } from '@src/constants'
import { useHasPermission } from '@helpers/hooks/useHasPermission'
import { Perm } from '@type/permissions'
import { getInitials } from '@helpers/utils'
import useGetAccount from '@helpers/hooks/useGetAccount'

import type { AddUserResponse } from '@models/User'
import { zIndex } from '@src/zIndex'

type TValue<IsMulti = false, T = unknown> = IsMulti extends false ? T : T[]

export interface UserSelectProps<IsMulti extends boolean = false, T = unknown> {
  value?: IsMulti extends false ? T : T[]
  label?: string
  required?: boolean
  errorMessage?: string
  onChange?: (value?: TValue<IsMulti, T>) => void
  onSelectOption?: (value: Option<T>) => void
  customOptions?: Option<T>[]
  filterOptions?: (option: Option<T>) => boolean
  menuIsOpen?: boolean
  styles?: StylesConfig<Option<T>, IsMulti, GroupBase<Option<T>>>
  excludeMe?: boolean
}

const LIST_HEADER_HEIGHT = 45

export function UserSelect<IsMulti extends boolean = false, T = unknown>({
  value,
  label,
  required = false,
  errorMessage,
  onChange,
  onSelectOption,
  isMulti,
  placeholder,
  name,
  className,
  customOptions = [],
  filterOptions,
  menuIsOpen,
  styles,
  excludeMe = false,
  ...props
}: Omit<AsyncProps<Option<T>, IsMulti, GroupBase<Option<T>>>, 'value' | 'onChange' | 'onSelectOption'> &
  UserSelectProps<IsMulti, T>) {
  const { t } = useTranslation('accounting', { keyPrefix: 'contract' })

  // The "Add user" / "Import users from {channel}" rows inside the
  // user-picker dropdown are only useful if the viewer can invite.
  const canInvite = useHasPermission(Perm.TEAM_INVITE_MEMBER)
  const account = useGetAccount()
  const [usedOptions, setUsedOptions] = useState<Option<T>[]>([])

  const connectedChannelType = useGetConnectedChannel()
  const connectedChannel = connectedChannelType
    ? communicationTypes[connectedChannelType as unknown as CommunicationType]
    : null
  const { handleImport, isLoading } = useImportUsersFromChannel()

  const queryClient = useQueryClient()

  const userIds = (Array.isArray(value) ? value : [value]) as Array<number>

  const { data: usedUsers } = useGetUsersByIds({
    ids: userIds,
    key: `[${userIds.join(',')}]`,
  })

  const getIsCustomOption = (data: Option<T>) => customOptions.some((option) => option.value === data.value)

  useEffect(() => {
    if (usedUsers?.results?.length) {
      setUsedOptions(
        (usedUsers?.results?.map((el) => ({
          value: el.id,
          label: el.full_name,
          email: el.email,
        })) as unknown as Option<T>[]) || [],
      )
    }
  }, [usedUsers?.results?.length])

  const loadOptions = useCallback(
    async (value: string) => {
      // Fetch exactly what react-select asked for (``value``). The previous
      // implementation called a ``refetch()`` bound to the ``inputValue``
      // state, which lags a render behind — so the server-side search ran
      // against the previous keystroke and the list never matched what was
      // typed. ``fetchQuery`` hits the same cache slot as the hook.
      const data = await queryClient.fetchQuery({
        queryKey: teamDropdownQueryKey(value),
        queryFn: () => fetchTeamDropdownList(value),
        cacheTime: Infinity,
        staleTime: Infinity,
      })
      let options: Option<T>[] =
        data?.results?.map((el) => ({
          label: el.full_name,
          value: el.id as T,
          roles: el.roles,
          sourceId: el.source_id,
          email: el.email,
        })) || []
      if (excludeMe) {
        options = options.filter((user) => user.value !== account?.team_user_id)
      }
      if (!value && !!customOptions?.length) {
        return [...customOptions, ...options]
      }
      return options
    },
    [excludeMe, queryClient, account?.team_user_id, customOptions],
  )
  // Re-create the debounced wrapper whenever ``loadOptions`` changes — i.e.
  // when ``excludeMe`` flips — otherwise the closure inside ``debounce``
  // continues to use the stale filter and keeps including/excluding the
  // wrong user even after the prop changes.
  const options = useMemo(() => debounce(loadOptions, 200), [loadOptions])

  const { handleTogglePopup, isPopupOpen } = usePopupHandlers()

  const handleAddUser = useCallback(
    (user: AddUserResponse) => {
      // Drop the cached dropdown pages so the freshly-added user shows up on
      // the next fetch (cacheTime is Infinity, so without this it wouldn't).
      queryClient.invalidateQueries({ queryKey: [OrgStructureKeys.OrgStructureDropdown] })
      if (isMulti) {
        onChange?.([...((value || []) as T[]), user.id] as TValue<IsMulti, T>)
      } else {
        onChange?.(user.id as TValue<IsMulti, T>)
      }
      onSelectOption?.({ label: user.full_name, value: user.id as T, roles: ['email_approver'], sourceId: null })
    },
    [value, queryClient, isMulti, onChange, onSelectOption],
  )

  const { handleChange, optionValue } = useSelectHelpers<T, IsMulti>(
    [...customOptions, ...usedOptions],
    value as TValue<IsMulti, T>,
    onChange,
  )

  const handleUserChange = (option: SingleValue<Option<T>> | MultiValue<Option<T>>) => {
    setUsedOptions(() => {
      if (!option) {
        return []
      }
      if (Array.isArray(option)) {
        return option
      }

      return [option]
    })
    if (isMulti) {
      const singleOption = (option as Option<T>[])[(option as Option<T>[]).length - 1]
      onSelectOption?.(singleOption)
    } else {
      onSelectOption?.(option as Option<T>)
    }
    handleChange(option)
  }

  const selectRef = useRef<SelectInstance<Option<T>>>()

  const customStyles = {
    multiValue: (base: CSSObjectWithLabel) =>
      Object.assign(base, {
        backgroundColor: 'transparent',
      }),
    menuPortal: (base: CSSObjectWithLabel) => ({ ...base, zIndex: zIndex.dropdown }),
  }

  const combinedStyles = {
    ...customStyles,
    ...styles,
  }

  return (
    <>
      <Select<Option<T>, IsMulti>
        styles={combinedStyles}
        menuIsOpen={menuIsOpen}
        menuPlacement="auto"
        defaultOptions
        cacheOptions
        loadOptions={options}
        value={optionValue || null}
        label={label}
        required={required}
        errorMessage={errorMessage}
        placeholder={placeholder}
        name={name}
        className={className}
        classNames={{
          control: () => 'cursor-text caret-transparent',
          menu: (classnames) => cx(classnames, '!w-[350px]'),
          ...props.classNames,
        }}
        refObj={selectRef as RefObject<SelectInstance<Option<T>>>}
        components={{
          MenuList: (props) => {
            const { maxHeight, ...menuListProps } = props
            // ``isSearchLoading`` is the async-select's options-fetch state.
            // ``isLoading`` (from the outer closure) is the channel-import
            // mutation state. They drive different visual elements; aliasing
            // the inner one avoids shadowing and keeps the import spinner
            // tied to import progress, not typing.
            const isSearchLoading = props.selectProps.isLoading
            // Substring match against the typed query — ``.includes`` rather
            // than ``.match`` because the latter coerces user input into a
            // regex, so typing ``(`` or ``*`` would either throw or match
            // unintended labels.
            const search = props.selectProps.inputValue?.toLowerCase()
            const hasOptions = search
              ? props.options.some(({ label }) => label?.toLowerCase().includes(search))
              : props.options.length > 0

            // While options are being fetched, render only a centered loading
            // indicator. Hiding the admin row and empty state during that
            // brief window avoids stacking "Add user / Import users /
            // Loading..." in the same vertical space — they all reappear the
            // moment the fetch resolves.
            if (isSearchLoading) {
              return (
                <div className="py-4 text-center text-grey_1" style={{ maxHeight }}>
                  Loading...
                </div>
              )
            }

            return (
              <div style={{ maxHeight: maxHeight }}>
                {!hasOptions && <div className="pt-2 text-center text-grey_1">{t('noUsersMessage')}</div>}
                {canInvite && (
                  <div className="flex justify-between">
                    <Button
                      type="contained"
                      buttonType="button"
                      className={cx('p-2 w-full justify-start')}
                      onClick={() => {
                        handleTogglePopup()
                        selectRef?.current?.blur()
                      }}
                    >
                      <IconCirclePlus className="h-4 w-4" /> {t('addUserLabel')}
                    </Button>
                    {connectedChannel && (
                      <div className="flex gap-1">
                        {isLoading && (
                          <span
                            className={cx('spinner-border absolute text-center')}
                            role="status"
                            aria-hidden="true"
                          />
                        )}
                        <Button type="contained" buttonType="button" disabled={isLoading} onClick={handleImport}>
                          Import users from {connectedChannel?.label}
                          <connectedChannel.Icon className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                )}
                {hasOptions && <div className="border-b mb-2 mx-2" />}
                <components.MenuList {...menuListProps} maxHeight={maxHeight - LIST_HEADER_HEIGHT} />
              </div>
            )
          },
          NoOptionsMessage: () => <></>,
          Option: (props) => {
            if (filterOptions && !filterOptions(props.data)) {
              return null
            }

            const { children, data } = props
            const isCustom = getIsCustomOption(data)

            const email = typeof data.email === 'string' ? data.email : ''
            const showEmail = !isCustom && !!email

            return (
              <components.Option
                {...props}
                innerProps={
                  { ...props.innerProps, 'data-qa': `${props.label}-option` } as React.DetailedHTMLProps<
                    React.HTMLAttributes<HTMLDivElement>,
                    HTMLDivElement
                  >
                }
              >
                <div className="flex items-center gap-2">
                  {isCustom ? (
                    <div className="w-5 h-5 border-blue-500 border-[1px] rounded-full flex justify-center items-center border-solid shrink-0">
                      <IconUser className="w-4 h-4 text-blue-500" />
                    </div>
                  ) : (
                    <div className="w-5 h-5 bg-blue-500 text-xs leading-5 rounded-full flex justify-center items-center text-white shrink-0">
                      {getInitials(data.label)}
                    </div>
                  )}
                  {showEmail ? (
                    <div className="min-w-0 flex-1 truncate">
                      <span>{children}</span>
                      <span className="text-grey_1 ml-1">({email})</span>
                    </div>
                  ) : (
                    <div>{children}</div>
                  )}
                </div>
              </components.Option>
            )
          },
          Placeholder: ({ children }) => {
            return (
              <div className="absolute inset-0 mx-4 flex items-center text-gray-500">
                <div className="w-6 h-6 border-neutral-500 border-[1px] rounded-full flex justify-center items-center border-dashed mr-2 shrink-0">
                  <IconUser className="w-4 h-4 text-gray-500" />
                </div>
                <div>{children}</div>
              </div>
            )
          },
          MultiValueLabel: (props) => {
            const { children, data } = props
            const isCustom = getIsCustomOption(data)

            return (
              <components.MultiValueLabel {...props}>
                <div className="flex items-center gap-2">
                  {isCustom ? (
                    <div className="w-4 h-4 border-blue-500 border-[1px] rounded-full flex justify-center items-center border-solid shrink-0">
                      <IconUser className="w-3 h-3 text-blue-500" />
                    </div>
                  ) : (
                    <div className="w-5 h-5 bg-blue-500 text-xs leading-5 rounded-full flex justify-center items-center text-white shrink-0">
                      {getInitials(data.label)}
                    </div>
                  )}
                  {children}
                </div>
              </components.MultiValueLabel>
            )
          },
          ClearIndicator: (props) => {
            return (
              <components.ClearIndicator {...props}>
                <IconX className="text-neutral-300 hover:stroke-black hover:opacity-60 transition w-4 h-4" stroke={4} />
              </components.ClearIndicator>
            )
          },
          ...props.components,
        }}
        onChange={handleUserChange}
        isMulti={isMulti}
        menuPortalTarget={document.body}
        isClearable={props.isClearable}
        isDisabled={props.isDisabled}
        onMenuOpen={props.onMenuOpen}
        onMenuClose={props.onMenuClose}
      />
      {isPopupOpen && <QuickUserAddPopup onClose={handleTogglePopup} onAddUser={handleAddUser} />}
    </>
  )
}

export default UserSelect
